/**
 * Media URL <-> id helpers, plus photo/video proof-type detection.
 *
 * The API stores media as `/api/v1/media/{uuid}` URLs, but the upload
 * components work with bare media ids. These convert between the two.
 */

/** Media type of a submission proof, as reported by the API. */
export type ProofMediaType = 'photo' | 'video';

/** Video file extensions treated as video proof when no explicit type is given. */
const VIDEO_PROOF_URL_RE = /\.(mp4|mov|webm|m4v)$/i;

/**
 * Derive the proof media type from a challenge's `type` / `answerType` pair.
 * Mirrors the API's `submissionMediaKind` — imported challenges often carry
 * `type: 'photo' | 'video'` while `answerType` stays at its `'text'` default,
 * and video wins when the two fields disagree.
 *
 * @param challenge - The challenge's `type` / `answerType` pair.
 * @returns `'photo' | 'video'` for media challenges, undefined otherwise.
 */
export function challengeMediaType(challenge: {
  type: string;
  answerType: string;
}): ProofMediaType | undefined {
  if (challenge.answerType === 'video' || challenge.type === 'video') return 'video';
  if (challenge.answerType === 'photo' || challenge.type === 'photo') return 'photo';
  return undefined;
}

/**
 * True when a submission's proof URL points at a video. Prefers an explicit
 * `mediaType` when the caller has one; otherwise falls back to sniffing the
 * URL's file extension (.mp4/.mov/.webm/.m4v, case-insensitive, tolerant of
 * query strings and fragments).
 *
 * @param url - The proof URL, or null/undefined when there is none.
 * @param mediaType - Explicit media type when known (wins over sniffing).
 * @returns Whether the proof should render in a `<video>` element.
 */
export function isVideoProofUrl(
  url: string | null | undefined,
  mediaType?: string | null,
): boolean {
  if (mediaType === 'video') return true;
  if (mediaType === 'photo') return false;
  if (!url) return false;
  const [path] = url.split(/[?#]/);
  return VIDEO_PROOF_URL_RE.test(path ?? url);
}

/**
 * Extract a media id from a media URL. Accepts a full `/api/v1/media/{id}`
 * URL or a bare UUID; returns undefined for anything else (incl. null/empty).
 *
 * @param url - Stored media URL, a bare id, or null/undefined.
 * @returns The media id, or undefined.
 */
export function extractMediaId(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  const match = url.match(/\/api\/v1\/media\/([a-f0-9-]+)$/i);
  if (match) return match[1];
  if (/^[a-f0-9-]{36}$/i.test(url)) return url;
  return undefined;
}

/**
 * Build a media URL from an id, or null when the id is absent (so the API
 * stores SQL NULL rather than an empty string).
 *
 * @param mediaId - Media id, or undefined.
 * @returns The media URL, or null.
 */
export function buildMediaUrl(mediaId: string | undefined): string | null {
  if (!mediaId) return null;
  return `/api/v1/media/${mediaId}`;
}
